import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { IMAGE_BASE_PATH } from "../../config/paths";
import { homeButtonStyle, homeDescriptionStyle } from "../../styles/home/home.styles";
import { useLinkLang } from "../../hooks/useLinkLang";

type Props = {
  t: (key: string) => string;
  heroDescId: string;
};

export default function BannerSection({ t, heroDescId }: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const linkLang = useLinkLang();

  const KITE_URL = `https://ridecore.com/${linkLang}/kite/kite-range`;
  const WING_URL = `https://ridecore.com/${linkLang}/wing/foil-and-wing-range`;

  // IDs semânticos p/ aria
  const heroTitleId = "home-hero-title";
  const heroKickerId = "home-hero-kicker";

  const heroTitle = `${t("ESPÍRITO")}\n${t("LATINO")}`; // será H1
  const heroIntro = t("HomePage.introducao");
  // const heroAmerica = t("HomePage.america");

  // Como a imagem/vídeo é "decorativo" (o texto já descreve),
  // o alt deve ser vazio para não poluir leitores de tela.
  // Se você fizer questão de descrever, aí sim use um alt descritivo.
  const decorativeAlt = "";

  return (
    <Box
      component="section"
      aria-labelledby={heroTitleId}
      aria-describedby={heroDescId}
      sx={{ position: "relative", height: "100%", overflow: "hidden" }}
    >
      {isMobile ? (
        <img
          src={`${IMAGE_BASE_PATH}home/HomeBanner2.webp`}
          alt={decorativeAlt}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
      ) : (
        <Box sx={{ width: "100%", height: "100%", position: "relative", overflow: "hidden" }}>
          {/* Poster decorativo para o primeiro paint */}
          <img
            src={`${IMAGE_BASE_PATH}home/VideoPlaceHolder.avif`}
            alt={decorativeAlt}
            aria-hidden="true"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              position: "absolute",
              zIndex: 1,
            }}
            loading="eager"
            fetchPriority="high"
            decoding="async"
          />

          <video
            src="https://firebasestorage.googleapis.com/v0/b/corelatam-6d704.appspot.com/o/Corehomevideo.mp4?alt=media&token=f87007dc-e8d4-47af-abac-d52224593d75"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster={`${IMAGE_BASE_PATH}home/VideoPlaceHolder.avif`}
            // vídeo é decorativo aqui; o conteúdo relevante está no texto
            aria-hidden="true"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onLoadedData={(e) => {
              const video = e.currentTarget;
              const placeholder = video.previousSibling as HTMLImageElement | null;
              if (placeholder) placeholder.style.display = "none";
            }}
          >
            {/* fallback básico */}
          </video>

          {/* Fallback acessível (não depende do <video>) */}
          <Typography
            component="p"
            sx={{
              position: "absolute",
              width: 1,
              height: 1,
              p: 0,
              m: -1,
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
          >
            {t("HomePage.videoFallback")}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          maxWidth: "100%",
          color: "#fff",
          textAlign: "center",
          px: 2,
          pb: { xs: 3, sm: 4, md: 6 },
          zIndex: 2,
        }}
      >
        {/* Links reais e com label acessível */}
        <Button
          component="a"
          href={KITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ...homeButtonStyle }}
          aria-label={t("HomePage.linkKiteAria") || "Abrir linha de kites CORE em nova aba"}
        >
          KITE
        </Button>

        <Box component="span" aria-hidden="true" sx={{ mx: 1 }}>
          |
        </Box>

        <Button
          component="a"
          href={WING_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ...homeButtonStyle }}
          aria-label={t("HomePage.linkWingAria") || "Abrir linha de wings CORE em nova aba"}
        >
          WING
        </Button>

        {/* Se você quiser manter o visual separado em 2 linhas, mas SEO quer H1 único */}
        <Typography
          id={heroTitleId}
          component="h1"
          gutterBottom={false}
          sx={{
            fontFamily: "PFDinTextPro-Thin, sans-serif",
            fontWeight: 400,
            fontSize: "3.5rem",
            lineHeight: 1.1,
            m: 0,
            textTransform: "uppercase",
            whiteSpace: "pre-line",
          }}
        >
          {heroTitle}
        </Typography>

        {/* Se quiser um “kicker” extra, use p ou h2 dependendo da hierarquia da página */}
        <Typography
          id={heroKickerId}
          component="p"
          sx={{
            ...homeDescriptionStyle,
            maxWidth: { xs: "330px", sm: "500px", md: "650px" },
            mx: "auto",
            mt: 2,
            textAlign: "center",
            fontFamily: "Helvetica,Arial,Verdana,sans-serif",
            fontWeight: 300,
            fontSize: "1.1rem",
          }}
        >
          {heroIntro}
        </Typography>

        {/* <Typography
          id={heroDescId}
          component="p"
          sx={{
            ...homeDescriptionStyle,
            maxWidth: { xs: "330px", sm: "500px", md: "650px" },
            mx: "auto",
            textAlign: "center",
            fontFamily: "Helvetica,Arial,Verdana,sans-serif",
            fontWeight: 300,
            fontSize: "1.3rem",
          }}
        >
          {heroAmerica}
        </Typography> */}
      </Box>
    </Box>
  );
}
